import React, { useEffect, useState } from 'react';
import {
  Body,
  Column,
  Container,
  DiagnoseContainer,
  Footer,
  InfoContainer,
  PatientInfo,
  Row,
} from './styles';
import Editor from '~/presentation/components/editor';
import { Menu } from '~/presentation/components/menu';
import { Button, Textarea } from '@wisecare-tech/design-system-web';
import { makeReduxActiveMessage } from '~/main/factories/usecases/message/Update';
import { HeaderSimples } from '~/presentation/components/header';
import { AlertMessage } from '~/presentation/components/messages/AlertMessage';
import { closeModal } from '~/utils/closeModal';
import { calculateAgeLaudo } from '~/utils/tableDateLaudo';
import InfoColapse from '../Diagnose/InfoColapse';
import ListDiagnosisFiles from '../Diagnose/ListDiagnosisFiles';
import { useParams } from 'react-router';
import { ListOnCallRequest } from '~/domain/usecases/onCallRequest/redux';
import { makeRemoteReportOnCallRequest } from '~/main/factories/usecases/onCallRequest/ReportOnCallRequestFactory';
import { getProfessionalInfo } from '~/utils/getProfessionalInfo';
import { makeRemoteAcceptOnCallSpecialist } from '~/main/factories/usecases/onCallSpecialist/AcceptOnCallSpecialistFactory';
import { History } from '~/main/routes';
import { makeRemoteJoinOnCallSpecialist } from '~/main/factories/usecases/onCallSpecialist/JoinOnCallSpecialistFactory';
import { formatCpfToSpecialChars } from '~/utils/formatCpfToSpecialChars';
import { makeRemoteDownloadOnCallRequestFileById } from '~/main/factories/usecases/onCallRequestFile/DownloadOnCallRequestFileByIdFactory';
import { makeRemoteListOnCallRequest } from '~/main/factories/usecases/onCallRequest/ListOnCallRequestFactory';
import { V4hSpin } from '~/presentation/components/spin';
import { makeRemoteGetClinicalDocs } from '~/main/factories/usecases/clinicalDocs/GetClinicalDocsFactory';
import { makeRemoteDownloadAllOnCallRequestFile } from '~/main/factories/usecases/onCallRequestFile/DownloadAllOnCallRequestFileFactory';
import { useSelector } from 'react-redux';
import { iStore } from '~/domain/interfaces/models';

interface ownProps {
  data: ListOnCallRequest.Model['records'][0] | undefined;
  edit: boolean;
  opinion?: string;
}

const NewReport: React.FC<ownProps> = ({ opinion }) => {
  const [editorValue, setEditorValue] = useState('');
  const [notes, setNotes] = useState('');
  const [evaluation, setEvaluation] = useState('');
  const [consultant, setConsultant] = useState<
    ListOnCallRequest.Model['records'][0]['consultant'] | undefined
  >(undefined);
  const [data, setData] = useState<
    ListOnCallRequest.Model['records'][0] | undefined
  >();
  const [loading, setLoading] = useState(true);

  const professional = getProfessionalInfo();

  let { id, specialty } = useParams<{ id: string; specialty: string }>();
  const { token } = useSelector((store: iStore) => store.auth.access);

  useEffect(() => {
    if (data?.consultant)
      setConsultant({
        ...data?.consultant,
        sex: data?.consultant.sex === 'MALE' ? 'M' : 'F',
      });

    setEditorValue(opinion ?? '');
    setEvaluation(data?.currentAppointment?.clinicalCondition ?? '');
    setNotes(data?.currentAppointment?.forwarding ?? '');
  }, [data, opinion]);

  useEffect(() => {
    setLoading(true);
    makeRemoteListOnCallRequest()
      .list({
        body: {
          filters: {
            id: Number(id),
          },
        },
      })
      .then(response => {
        setData(response.records[0]);
      })
      .catch(error => {
        console.log('>>> error', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    if (data?.currentAppointment.medicalOpinion) {
      setLoading(true);
      makeRemoteGetClinicalDocs()
        .get({
          docId: Number(data.currentAppointment.medicalOpinion),
        })
        .then(response => {
          setEditorValue(response.content.opinion ?? '');
        })
        .catch(error => {
          console.log('>>> error', error);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [data]);

  return (
    <Container>
      <Menu />
      <HeaderSimples title="Parecer Médico" />

      <Body>
        <Column width="60%">
          <PatientInfo>
            <Row>
              <div className="name left">
                <strong>Nome:</strong>
                {` ${consultant?.fullname}`}
              </div>
              <div className="right">
                <strong>Sexo:</strong>{' '}
                {`${consultant?.sex === 'M' ? 'Masculino' : 'Feminino'}`}
              </div>
            </Row>
            <Row className="justify">
              <div className="left">
                <strong>Data de nascimento: </strong>
                {calculateAgeLaudo(consultant?.birthdate ?? '')}
              </div>
              {consultant?.docType === 'CPF' ? (
                <div className="right">
                  <strong>{consultant?.docType}: </strong>
                  {formatCpfToSpecialChars(consultant?.docValue)}
                </div>
              ) : (
                <div className="right">
                  <strong>{consultant?.docType} </strong> {consultant?.docValue}
                </div>
              )}
            </Row>
          </PatientInfo>
          <DiagnoseContainer>
            <div className="title">Parecer</div>
            <div className="editor">
              <Editor value={editorValue} onChange={setEditorValue} />
            </div>
          </DiagnoseContainer>
        </Column>
        <Column width="40%">
          <InfoContainer>
            <InfoColapse
              title="Subjetivo e Objetivo"
              children={<div>{data?.description}</div>}
            ></InfoColapse>
            <InfoColapse
              title="Avaliação e Plano de Cuidado"
              children={
                <Textarea
                  onChange={e => setEvaluation(e.target.value)}
                  placeholder="Escreva aqui o plano de cuidado do paciente."
                  value={evaluation}
                />
              }
            ></InfoColapse>
            <InfoColapse
              title="Anotações"
              children={
                <Textarea
                  onChange={e => setNotes(e.target.value)}
                  placeholder="Escreva aqui anotações sobre o paciente."
                  value={notes}
                />
              }
            ></InfoColapse>

            {data && (
              <InfoColapse
                title="Arquivos"
                children={
                  <ListDiagnosisFiles
                    id={data.id}
                    files={data?.files}
                    downloadFile={(file: number, id: number) => {
                      return makeRemoteDownloadOnCallRequestFileById().download(
                        {
                          fileId: file,
                          requestId: id,
                          onCallId: Number(specialty),
                        },
                      );
                    }}
                    downloadAllFiles={(id: number) => {
                      return makeRemoteDownloadAllOnCallRequestFile().download({
                        body: {
                          accessToken: token ?? '',
                        },
                        onCallId: Number(specialty),
                        requestId: id,
                      });
                    }}
                  />
                }
              ></InfoColapse>
            )}
          </InfoContainer>
        </Column>
      </Body>
      <Footer>
        <div>
          <Button
            size="md"
            variant="outlinedDestructive"
            onClick={() => {
              makeReduxActiveMessage().active({
                active: 'cancelMedicalOpinion',
                actionCancel: closeModal,
                actionOk: () => {
                  History.getHistory().goBack();
                  closeModal();
                },
              });
            }}
          >
            Cancelar
          </Button>
        </div>
        <div>
          <Button
            size="md"
            variant="outlined"
            onClick={() => {
              makeReduxActiveMessage().active({
                active: 'startInterconsultMedicalOpinion',
                actionCancel: closeModal,
                actionOk: () => {
                  if (professional)
                    makeRemoteAcceptOnCallSpecialist()
                      .accept({
                        onCallId: Number(specialty),
                        specialistId: professional?.id,
                        requestId: Number(id),
                        body:
                          evaluation || notes
                            ? {
                                clinicalCondition: evaluation,
                                forwarding: notes,
                              }
                            : undefined,
                      })
                      .then(() => {
                        makeRemoteJoinOnCallSpecialist()
                          .join({
                            onCallId: Number(specialty),
                            specialistId: professional?.id,
                            requestId: Number(id),
                          })
                          .then(response => {
                            History.getHistory().push('/conf', {
                              appointmentId: response.appointmentId,
                              onCall: {
                                sessionName: response.sessionName,
                                id: Number(specialty),
                                name: Number(specialty).toString(),
                                specialist: professional?.id,
                              },
                            });
                          })
                          .catch(() => {
                            AlertMessage({
                              message: 'Erro ao entrar na conf!',
                              type: 'danger',
                            });
                          });
                      })
                      .catch(() => {
                        AlertMessage({
                          message: 'Erro ao aceitar solicitação!',
                          type: 'danger',
                        });
                      });
                  closeModal();
                },
              });
            }}
          >
            Iniciar Interconsulta
          </Button>
          <Button
            size="md"
            variant="primary"
            onClick={() => {
              if (professional)
                makeRemoteReportOnCallRequest()
                  .report({
                    onCallId: Number(specialty),
                    specialistId: professional?.id,
                    requestId: Number(id),
                    body: {
                      opinion: editorValue,
                      clinicalCondition: evaluation,
                      forwarding: notes,
                    },
                  })
                  .then(res => {
                    makeReduxActiveMessage().active({
                      active: 'evaluateMedicalOpinion',
                      actionCancel: closeModal,
                      data: {
                        appointmendtId: data?.currentAppointment?.id,
                        docId: res.clinicalDoc,
                      },
                    });
                  })
                  .catch(() => {
                    AlertMessage({
                      title: 'Erro',
                      message: 'Erro ao concluir parecer',
                      type: 'danger',
                    });
                  });
            }}
            disabled={!editorValue.length}
          >
            Concluir e assinar parecer
          </Button>
        </div>
      </Footer>

      {loading && <V4hSpin fullScreen size="40px" />}
    </Container>
  );
};

export default NewReport;
